/**
 * math-exam-launch — 數學考卷的選卷 / 動作分流(複習中心接點)。
 *
 * 對應 listening-exam-launch.ts(英語聽力卷)同一套:複習中心「數學考卷」卡 →
 *   - 「出一份數學考卷(重抽)」:換 seed 用產生器 `generateMathExam` 生新卷。
 *   選卷後 →「線上作答」`runMathExam`(即時批改 + 錯題回流) /「列印 A4」開新視窗。
 *
 * 視覺 = 學術風格設計系統(pageShell / academicCard / academicButton),專業介面
 * (無音效 / mascot / 彩光)。學習/遊戲分離:純學習側,絕不碰 commanderXp / 戰鬥數值。
 */

import { pageShell, academicCard, academicButton } from '../ui/components';
import { mountEscapeButton } from '../escape-button';
import { generateMathExam } from './math-exam-gen';
import type { MathExam, MathExamItem, MathSectionKind } from './math-exam-gen';
import { runMathExam } from './math-exam';
import type { MathExamScore, MathExamWrong } from './math-exam';
import { generateMathExamHtml } from '../printable/pdf-gen';
import { loadProgress, saveProgress } from '../../shared/progress';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { feedMistakesToWiki } from './wiki-bridge';

/**
 * 各大題對應的錯題本 KP(名實相符 —— 錯題回流到對的概念,避免難度錯配)。
 *   - compute 直式除法(整除/有餘)→ 有餘數的除法。
 *   - mulvert 乘法直式 → 三位數×一位數直式。
 *   - arrow / inverse 乘除 round-trip / 互逆 → 乘除關係與應用解題。
 *   - remword 有餘數應用(進位/捨去)→ 有餘數的除法。
 *   - twostep 真兩步驟 → 兩步驟文字題。
 *   - word 單步驟應用 → 乘除關係與應用解題(舊版誤映 two-step,已修正)。
 */
const SECTION_KP: Record<MathSectionKind, string> = {
  compute: 'math.div.remainder',
  mulvert: 'math.mul.3x1',
  arrow: 'math.div.mul-relation',
  inverse: 'math.div.mul-relation',
  remword: 'math.div.remainder',
  twostep: 'math.word.two-step',
  word: 'math.div.mul-relation',
};

/** 列印一份數學考卷:開新視窗 write A4 HTML(沿用 pdf-gen 的 window.open 模式)。 */
function openPrintWindow(exam: MathExam): void {
  const html = generateMathExamHtml(exam);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

/**
 * 重抽用 seed:時間戳 + 單調遞增計數,保證連續重抽(即使同一毫秒)永遠換新卷。
 * 仍可重現:同 seed → 同卷(seed 本身決定內容)。
 */
let reshuffleCounter = 0;
function freshSeed(): number {
  reshuffleCounter = (reshuffleCounter + 1) % 1_000_000;
  return (Math.floor(Date.now()) + reshuffleCounter * 7919) % 1_000_000;
}

/** 一題的正解字串(回流錯題本用)。 */
function answerOf(item: MathExamItem): string {
  return item.answer;
}

/**
 * 交卷後把答錯題回流:寫進本機錯題本 + best-effort 餵 student wiki。
 * 純學習側,fire-and-forget(wiki 失敗不阻斷)。
 */
function flowBackWrong(wrong: MathExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return; // 未建立進度檔(訪客)→ 靜默跳過,不阻斷批改。
  const feeds = [];
  for (const w of wrong) {
    const kp = SECTION_KP[w.sectionKind];
    const answer = answerOf(w.item);
    recordMistake(progress, {
      questionId: deriveQuestionId(w.item.prompt),
      kp,
      subject: 'math',
      prompt: w.item.prompt,
      correctAnswer: answer,
      userAnswer: null,
    });
    feeds.push({ subject: 'math' as const, kp, prompt: w.item.prompt, correctAnswer: answer });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

/**
 * Render 數學考卷選卷畫面 into `container`。
 *
 * @param container 掛載點。
 * @param onDone    離開回呼(回複習中心)。
 */
export function runMathExamLaunch(
  container: HTMLElement,
  onDone: () => void,
): void {
  renderMenu(container, onDone);
}

function renderMenu(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: '數學考卷',
    subtitle: '參考段考真卷題型(直式除法 / 算式填空 / 乘除互換 / 應用題)自動出整張卷。可線上作答或列印 A4。',
  });

  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    onDone();
  });

  const root = document.createElement('div');
  root.className = 'mx-launch';
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  const newCard = academicCard({
    icon: '🎲',
    label: '出一份數學考卷(重抽)',
    sub: '從題型範本隨機生一份整張數學考卷(每次不同,答案程式可驗證)',
    onClick: () => {
      unmountEscape();
      const exam = generateMathExam({ seed: freshSeed() });
      renderExamActions(container, exam, onDone);
    },
  });
  newCard.id = 'mx-launch-new';
  newCard.classList.add('mx-launch-card');

  root.append(newCard);
}

/** 選卷後:線上作答 / 列印 / 換一份 的動作頁。 */
function renderExamActions(
  container: HTMLElement,
  exam: MathExam,
  onDone: () => void,
): void {
  container.innerHTML = '';

  const shell = pageShell(container, {
    title: exam.title,
    subtitle: '選擇線上作答(即時批改 + 錯題回流)或列印 A4 紙卷(筆寫、附解答頁)。',
  });

  const back = (): void => renderMenu(container, onDone);
  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    back();
  });

  const root = document.createElement('div');
  root.className = 'mx-actions-page';
  root.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;';
  shell.body.appendChild(root);

  const onlineBtn = academicButton({
    label: '線上作答',
    variant: 'primary',
    onClick: () => {
      unmountEscape();
      runMathExam(
        container,
        exam,
        () => renderExamActions(container, exam, onDone),
        (_score: MathExamScore, wrong: MathExamWrong[]) => flowBackWrong(wrong),
      );
    },
  });
  onlineBtn.id = 'mx-action-online';

  const printBtn = academicButton({
    label: '🖨️ 列印 A4',
    variant: 'secondary',
    onClick: () => openPrintWindow(exam),
  });
  printBtn.id = 'mx-action-print';

  const reshuffleBtn = academicButton({
    label: '🎲 重抽一份',
    variant: 'secondary',
    onClick: () => {
      unmountEscape();
      const fresh = generateMathExam({ seed: freshSeed() });
      renderExamActions(container, fresh, onDone);
    },
  });
  reshuffleBtn.id = 'mx-action-reshuffle';

  const backBtn = academicButton({
    label: '← 返回',
    variant: 'secondary',
    onClick: () => {
      unmountEscape();
      back();
    },
  });
  backBtn.id = 'mx-action-back';

  root.append(onlineBtn, printBtn, reshuffleBtn, backBtn);
}
